import ms, { type StringValue } from "ms";
import { z } from "zod";
import { defineStepConfig } from "../registry/helpers";

const isMsParseable = (val: string): boolean => {
  try {
    return ms(val as StringValue) != null;
  } catch {
    return false;
  }
};

/**
 * Schedule Trigger Step
 *
 * Entry point for workflows triggered on a cron schedule.
 * Uses Temporal's Schedules API for reliable, durable scheduling.
 */
export const scheduleTriggerStep = defineStepConfig({
  type: "schedule_trigger",
  name: "Schedule Trigger",
  description: "Triggers workflow on a cron schedule using Temporal Schedules",
  category: "Trigger",

  // No inputs - this is an entry point. scheduledTime comes via context.
  inputSchema: z.never(),

  outputSchema: z.object({
    scheduledTime: z
      .string()
      .describe("ISO timestamp of the scheduled fire time"),
  }),

  configSchema: z.object({
    cronExpression: z
      .string()
      .default("0 0 * * *")
      .describe("Cron expression (e.g., '0 0 * * *' for midnight daily)"),
    timezone: z
      .string()
      .default("UTC")
      .describe("IANA timezone (e.g., 'America/New_York')"),
    description: z.string().optional().describe("Human-readable description"),
    overlapPolicy: z
      .enum(["skip", "buffer_one", "allow_all", "cancel_other"])
      .default("skip")
      .describe("What to do if previous run is still executing"),
    workflowExecutionTimeout: z
      .string()
      .optional()
      .refine((val) => val === undefined || isMsParseable(val), {
        message:
          "Must be a ms-parseable duration string (e.g., '2 hours', '30 minutes', '90s')",
      })
      .describe(
        "Max workflow execution time as ms-parseable string (e.g., '2 hours'). Defaults to 1 hour.",
      ),
  }),

  emits: [] as const,
});

export type ScheduleTriggerConfig = z.infer<
  typeof scheduleTriggerStep.configSchema
>;
